import { RefundIcon } from "@images/svgIcons/money/refundIcon"
import { word } from "@modules/core/utils/i18n"
import { HistoryDetails } from "@modules/finances/components/historyDetails"
import { InvoiceDetails } from "@modules/finances/components/invoiceDetails"
import { InvoiceDetailsTable } from "@modules/finances/components/invoiceDetailsTable"
import { TransactionRefundModal, TransactionRefundModalRef } from "@modules/finances/components/transactionsRefundModal"
import { TransactionDetailsEntity } from "@modules/finances/entities/transactionDetailsEntity"
import { ApiFinanceType } from "@modules/finances/types/financesType"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUButton } from "@modules/ui/components/huButton"
import { HUNavigateButton } from "@modules/ui/components/huNavigateButton"
import { useToast } from "@modules/ui/components/huToast"
import { MenuItem } from "primereact/menuitem"
import { TabMenu } from "primereact/tabmenu"
import { useMemo, useRef, useState } from "react"
import { useTheme } from "styled-components"

type TransactionsDetailsProps = { transactionDetails: TransactionDetailsEntity; apiFinanceType: ApiFinanceType }

export const TransactionsDetailsContainer: React.FC<TransactionsDetailsProps> = ({
  transactionDetails,
  apiFinanceType,
}) => {
  const [isDownloadingInvoiceFile, setIsDownloadingInvoiceFile] = useState(false)
  const theme = useTheme()
  const toast = useToast()
  const { getCurrentStackState, setCurrentStackState } = useSidebar()
  const isRefundDisabled = !transactionDetails.refundable
  const colorIcon = isRefundDisabled ? theme.neutralColor300 : theme.blackAtheleWhiteOwner

  const transactionRefundModal = useRef<TransactionRefundModalRef>(null)
  const activeTabIndex = getCurrentStackState("activeTabIndex") ?? 0
  const language = useLanguage()
  const transactionsOptions = useMemo<MenuItem[]>(
    () => [
      {
        label: word("finances.invoice"),
      },
      {
        label: word("finances.history"),
      },
    ],
    [language],
  )

  const fetchInvoice = () => {
    if (transactionDetails.invoicePdfUrl) {
      try {
        setIsDownloadingInvoiceFile(true)
        const link = document.createElement("a")
        link.download = transactionDetails.invoiceNumber + ".pdf"
        link.href = transactionDetails.invoicePdfUrl
        link.click()
      } catch (e) {
        toast.show({
          severity: "error",
          summary: word("global.error.label"),
        })
      } finally {
        setTimeout(() => setIsDownloadingInvoiceFile(false), 1000) // Simulate loading hack
      }
    }
  }

  return (
    <>
      <div className="flex flex-column mt-4">
        <TabMenu
          model={transactionsOptions}
          activeIndex={activeTabIndex}
          onTabChange={(e) => setCurrentStackState("activeTabIndex", e.index)}
        />
        {activeTabIndex === 0 && (
          <div className="flex flex-column mb-5">
            <InvoiceDetails transactionDetails={transactionDetails} />
            <InvoiceDetailsTable transactionDetails={transactionDetails} />
            <div className="flex mt-4 justify-content-between">
              {!isRefundDisabled && (
                <HUButton
                  type="Right_Icon"
                  icon={{
                    iconView: <RefundIcon color={colorIcon} />,
                    iconSize: 14,
                  }}
                  size="M"
                  colorType="Quaternary"
                  text={word("box.transaction.refund.modal.alert.action")}
                  onClick={() => {
                    transactionRefundModal.current?.open()
                  }}
                  className="h-3rem"
                />
              )}

              {transactionDetails.invoicePdfUrl ? (
                <div className="flex ml-auto">
                  <HUNavigateButton
                    link={false}
                    to={transactionDetails.invoicePdfUrl}
                    label={word("finances.array.invoice.download")}
                    icon={{
                      iconView: (
                        <span
                          className={`p-button-icon p-c ${
                            isDownloadingInvoiceFile ? "pi pi-spin pi-spinner" : "pi pi-download"
                          }`}
                        />
                      ),
                    }}
                    iconPosition="right"
                    onPress={fetchInvoice}
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}
        {activeTabIndex === 1 && (
          <HistoryDetails transactionDetails={transactionDetails} apiFinanceType={apiFinanceType} />
        )}
      </div>
      <TransactionRefundModal transactionDetails={transactionDetails} ref={transactionRefundModal} />
    </>
  )
}
